"use client";

import { Play } from "lucide-react";
import dynamic from "next/dynamic";
import { type FC, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";

// Dynamically import InlineVideo with SSR disabled
// This prevents video source URLs from appearing in the initial HTML,
// which stops browser preload scanner from fetching all videos eagerly.
// The player only mounts after it scrolls into view, keeping the bundle
// and video fetches out of the initial load path.
const InlineVideo = dynamic(
  () => import("./inline-video").then((mod) => mod.InlineVideo),
  { ssr: false }
);

type LazyInlineVideoProps = {
  src: string;
  caption?: string;
  poster?: string;
  className?: string;
  width?: number;
  height?: number;
};

export const LazyInlineVideo: FC<LazyInlineVideoProps> = ({
  src,
  caption,
  poster,
  className,
  width,
  height,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const aspectRatio = width && height ? `${width} / ${height}` : "16 / 9";
  const fallbackAlt = caption ?? "Video poster";
  const posterWidth = width ?? 1280;
  const posterHeight = height ?? 720;

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsLoaded(true);
      return;
    }

    const element = containerRef.current;

    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.01 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isLoaded]);

  if (isLoaded) {
    return (
      <InlineVideo
        caption={caption}
        className={className}
        poster={poster}
        src={src}
      />
    );
  }

  return (
    <figure className={cn("space-y-2", className)}>
      <div
        className={cn(
          "group relative w-full overflow-hidden rounded-2xl border border-fd-border/70",
          "bg-fd-muted/20 shadow-sm"
        )}
        ref={containerRef}
      >
        <div className="relative w-full" style={{ aspectRatio }}>
          {poster ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={fallbackAlt}
              className="absolute inset-0 h-full w-full object-cover"
              decoding="async"
              height={posterHeight}
              loading="lazy"
              src={poster}
              width={posterWidth}
            />
          ) : (
            <div className="absolute inset-0 bg-fd-muted/40" />
          )}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-fd-background/80 px-4 py-2 font-semibold text-fd-foreground text-sm shadow-sm ring-1 ring-fd-border/70 backdrop-blur">
            <Play aria-hidden="true" className="h-4 w-4" />
            Video loads on view
          </span>
        </div>
      </div>
    </figure>
  );
};
